import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, IconButton, Button } from "@mui/material";
import { InfoOutlined, FileUploadOutlined, Close } from "@mui/icons-material";
import { UPLOAD_CRITERIA } from "./data";
import LocationCard from "../location-card";
import AddressFormFields from "../../../../common/components/address-component/address-component";
import {
  addServiceableLocation,
  updateBusinessAddress,
  uploadPreneedMedia,
  deleteGalleryImages,
  deleteServiceableLocation,
} from "../../../../api/preneed-section/preneed-profile-mgmt.api";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import ActionConfirmationPopup from "../../../../common/components/action-confirmation-popup/action-confirmation-popup";
import { getCityCoordinates } from "../../../../api/mapbox";
import "./location-details.scss";

interface UploadedImage {
  file: File;
  preview: string;
}
interface LocationDetailsProps {
  data: any;
  refresh?: () => void;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({ data, refresh }) => {
  const [addedLocations, setAddedLocations] = useState(
    data?.serviceableLocations,
  );
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [gallery, setGallery] = useState(data?.plannerMedia || []);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string[]>([]);
  const [imageCountError, setImageCountError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [infoPopup, setInfoPopup] = useState({
    open: false,
    title: "",
    note: "",
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null as number | null,
    isLocal: false,
    index: -1,
    type: "" as "image" | "location" | "",
  });

  useEffect(() => {
    setGallery(data?.plannerMedia || []);
    setAddedLocations(data?.serviceableLocations || []);
  }, [data]);

  const businessAddressInitialValues = {
    businessAddress: {
      address: data?.business_address?.businessAddress ?? "",
      city: data?.business_address?.city ?? "",
      state: data?.business_address?.state ?? "",
      zipcode: data?.business_address?.zipcode ?? "",
      latitude: data?.business_address?.latitude ?? null,
      longitude: data?.business_address?.longitude ?? null,
    },
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_IMAGES = 5;
  const VALID_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

  const handleRemoveLocation = (id: number) => {
    handleDeleteClick(-1, false, id, "location");
  };

  const handleImageUpload = async (files: FileList | File[] | null) => {
    setFileTypeError(null);
    if (!files) return;

    const newFiles = Array.isArray(files) ? files : Array.from(files);

    const validTypeImages = newFiles.filter((file) =>
      VALID_IMAGE_TYPES.includes(file.type),
    );

    const invalidTypeFiles = newFiles.filter(
      (file) => !VALID_IMAGE_TYPES.includes(file.type),
    );

    const oversizedFiles = validTypeImages.filter(
      (file) => file.size > MAX_FILE_SIZE,
    );

    if (invalidTypeFiles.length > 0) {
      setFileTypeError("Only image files (JPEG, JPG, PNG, WebP, SVG, GIF) are allowed.");
    }

    if (oversizedFiles.length > 0) {
      setFileSizeError((prev) => [
        ...prev,
        ...oversizedFiles.map((file) => file.name),
      ]);
    }

    const validFiles = validTypeImages.filter(
      (file) => file.size <= MAX_FILE_SIZE,
    );

    const totalImages = uploadedImages.length + validFiles.length;
    if (totalImages > MAX_IMAGES) {
      setImageCountError(`Maximum ${MAX_IMAGES} images allowed.`);
      return;
    }

    const newImages: UploadedImage[] = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImages((prev) => [...prev, ...newImages]);

    if (validFiles.length > 0) {
      try {
        const formData = new FormData();
        validFiles.forEach((file) => formData.append("images", file));

        const response = await uploadPreneedMedia(formData);

        if (response.data.success) {
          console.log("Images uploaded successfully");
          setUploadedImages([]);
          if (refresh) refresh();
        } else {
          setInfoPopup({
            open: true,
            title: "Error",
            note: response.data.details || response.data.message || "Failed to upload images.",
          });
          // Remove the specific images that failed to upload
          setUploadedImages((prevArray) =>
            prevArray.filter((img) => !newImages.some((ni) => ni.preview === img.preview))
          );
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        const errorMessage = error.response?.data?.details || error.response?.data?.message || "Error uploading images. Please try again.";
        setInfoPopup({
          open: true,
          title: "Error",
          note: errorMessage,
        });
        // Remove the specific images that failed to upload
        setUploadedImages((prevArray) =>
          prevArray.filter((img) => !newImages.some((ni) => ni.preview === img.preview))
        );
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const removed = uploadedImages[index];

    setUploadedImages((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      setImageCountError(null);
      return updated;
    });

    if (removed && fileSizeError.includes(removed.file.name)) {
      setFileSizeError((prev) =>
        prev.filter((name) => name !== removed.file.name),
      );
    }
  };

  const handleDeleteClick = (
    index: number,
    isLocal: boolean,
    id?: number,
    type: "image" | "location" = "image",
  ) => {
    setDeleteConfirm({
      open: true,
      id: id || null,
      isLocal,
      index,
      type,
    });
  };

  const handleConfirmDelete = async () => {
    const { isLocal, index, id, type } = deleteConfirm;
    if (type === "image") {
      if (isLocal) {
        handleRemoveImage(index);
      } else if (id) {
        try {
          const response = await deleteGalleryImages(id);
          if (response.data.success) {
            setGallery((prev: any[]) =>
              prev.filter((img: any) => img.fileId !== id),
            );
          } else {
            setInfoPopup({
              open: true,
              title: "Error",
              note: "Failed to delete image.",
            });
          }
        } catch (error) {
          console.error("Delete image error:", error);
          setInfoPopup({
            open: true,
            title: "Error",
            note: "Error deleting image. Please try again.",
          });
        }
      }
    } else if (type === "location" && id) {
      try {
        const response = await deleteServiceableLocation(id);
        if (response.data.success) {
          if (refresh) refresh();
          setAddedLocations((prev: any[]) =>
            prev.filter((location: any) => location.id !== id),
          );
        } else {
          setInfoPopup({
            open: true,
            title: "Error",
            note: "Failed to delete location.",
          });
        }
      } catch (error) {
        console.error("Delete location error:", error);
        setInfoPopup({
          open: true,
          title: "Error",
          note: "Error deleting location. Please try again.",
        });
      }
    }
    setDeleteConfirm({
      open: false,
      id: null,
      isLocal: false,
      index: -1,
      type: "",
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImageUpload(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  // const handleSaveLocation = async () => {
  //   try {
  //     const response = await addServiceableLocation(data);

  //     if (response.data.success) {
  //     }
  //   } catch (error) {
  //     console.error("Error in adding servicable locaiton:", error);
  //   }
  // };

  return (
    <Box className="location-details">
      {/* Business Address */}
      <Box className="location-details__section location-details__section--address">
        <Typography className="location-details__title">
          Business Address
        </Typography>
        <Typography className="location-details__description">
          This is your officially registered business address.
        </Typography>

        <Formik
          initialValues={businessAddressInitialValues}
          enableReinitialize={true}
          validationSchema={Yup.object({
            businessAddress: Yup.object({
              city: Yup.string().required("City is required"),
              state: Yup.string().required("State is required"),
              zipcode: Yup.string().required("Zipcode is required"),
              address: Yup.string()
                .required("Business Address is required")
                .min(3, "Min 3 characters required")
                .max(200, "Max 200 characters allowed"),

              //  new
              latitude: Yup.number()
                .nullable()
                .typeError("Latitude must be a number")
                .min(-90, "Latitude must be between -90 and 90")
                .max(90, "Latitude must be between -90 and 90")
                .required("Latitude is required"),

              longitude: Yup.number()
                .nullable()
                .typeError("Longitude must be a number")
                .min(-180, "Longitude must be between -180 and 180")
                .max(180, "Longitude must be between -180 and 180")
                .required("Longitude is required"),
            }),
          })}
          onSubmit={async (values, { resetForm }) => {
            try {
              const { city, state, zipcode, address } = values.businessAddress;

              let latitude: number | null = null;
              let longitude: number | null = null;

              try {
                const query = `${city}, ${state} ${zipcode}`;
                const mapboxRes = await getCityCoordinates(query);

                if (mapboxRes?.data?.features?.length > 0) {
                  const [lon, lat] = mapboxRes.data.features[0].center;
                  latitude = lat;
                  longitude = lon;
                }
              } catch (err) {
                console.error("Error fetching coordinates from Mapbox:", err);
              }

              if (latitude == null || longitude == null) {
                setInfoPopup({
                  open: true,
                  title: "Coordinates not found",
                  note: "Could not fetch latitude/longitude for this address. Please verify City/State/Zipcode and try again.",
                });
                return;
              }

              const reqBody = {
                businessAddress: address,
                city,
                state,
                zipcode,
                latitude,
                longitude,
              };

              const response = await updateBusinessAddress(reqBody);

              if (response.data.success) {
                resetForm({
                  values: {
                    businessAddress: {
                      ...values.businessAddress,
                      latitude,
                      longitude,
                    },
                  },
                });

                if (refresh) {
                  refresh();
                }
              } else {
                setInfoPopup({
                  open: true,
                  title: "Error",
                  note:
                    response.data.details ||
                    response.data.message ||
                    "Failed to update business address.",
                });
              }
            } catch (error: any) {
              console.error("Error in updating business address:", error);
              setInfoPopup({
                open: true,
                title: "Error",
                note:
                  error?.response?.data?.details ||
                  error?.response?.data?.message ||
                  "Error updating business address. Please try again.",
              });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            resetForm,
            handleChange,
            handleBlur,
            dirty,
          }) => {
            const { city, state, zipcode, address, latitude, longitude } = values.businessAddress;

            const isSaveDisabled =
              !!errors.businessAddress?.address ||
              !!errors.businessAddress?.city ||
              !!errors.businessAddress?.state ||
              !!errors.businessAddress?.zipcode ||
              !!errors.businessAddress?.latitude ||
              !!errors.businessAddress?.longitude ||
              !city ||
              !state ||
              !zipcode ||
              !address ||
              latitude == null ||
              longitude == null ||
              !dirty;

            return (
              <Form>
                <AddressFormFields
                  namePrefix="businessAddress"
                  displayStreetLines={false}
                  displayCountry={false}
                  displayBusinessAddress={true}
                  postCode="zipcode"
                  requiredFields={{
                    city: true,
                    state: true,
                    zipcode: true,
                    address: true,
                  }}
                  isProvider={true}
                />

                {!isSaveDisabled && (
                  <Box mt={2}>
                    <Button
                      type="submit"
                      disabled={isSaveDisabled}
                      className={`location-details__add-location-btn ${!isSaveDisabled ? "enabled" : ""
                        }`}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Form>
            );
          }}
        </Formik>
      </Box>

      {/* Serviceable Locations */}
      <Box className="location-details__section location-details__section--serviceable">
        <Typography className="location-details__title">
          Serviceable Locations
        </Typography>
        <Typography className="location-details__serviceable-description">
          Please select the ZIP code followed by the city in which your services are offered
        </Typography>

        <Formik
          initialValues={{
            businessAddress: {
              city: "",
              state: "",
              zipcode: "",
            },
          }}
          onSubmit={(values) => {
            console.log("LOG VALUES::", values);
          }}
        >
          {({ values, resetForm }) => {
            const { city, state, zipcode } = values.businessAddress;

            const isAddDisabled = !city || !state || !zipcode;

            const handleAddLocation = async () => {
              if (isAddDisabled) return;

              // prevent duplicate locations
              const alreadyExists = (addedLocations || []).some(
                (loc: any) =>
                  loc.zipcode === zipcode && loc.city === city && loc.state === state,
              );

              if (alreadyExists) {
                setInfoPopup({
                  open: true,
                  title: "Location already exists",
                  note: "This location has already been added to your serviceable locations.",
                });
                return;
              }

              let latitude: number | null = null;
              let longitude: number | null = null;

              try {
                const query = `${city}, ${state} ${zipcode}`;
                const mapboxRes = await getCityCoordinates(query);

                if (mapboxRes?.data?.features?.length > 0) {
                  const [lon, lat] = mapboxRes.data.features[0].center;
                  latitude = lat;
                  longitude = lon;
                }
              } catch (error) {
                console.error("Error fetching coordinates from Mapbox:", error);
              }

              const payload = { city, zipcode, state, latitude, longitude };

              const response = await addServiceableLocation(payload);

              if (response.data.success) {
                if (refresh) refresh();
                else {
                  setAddedLocations((prev: any[]) => [
                    ...prev,
                    {
                      id: response.data.data?.id || Date.now(),
                      zipcode,
                      city,
                      state,
                      latitude,
                      longitude,
                    },
                  ]);
                }

                resetForm();
              } else {
                setInfoPopup({
                  open: true,
                  title: "Error",
                  note:
                    response.data.details ||
                    response.data.message ||
                    "Failed to add location.",
                });
              }
            };

            return (
              <Form>
                <AddressFormFields
                  namePrefix="businessAddress"
                  displayStreetLines={false}
                  displayCountry={false}
                  postCode="zipcode"
                  requiredFields={{ zipcode: true, city: true, state: true }}
                  isProvider={true}
                />
                {/* ADD LOCATION BUTTON */}
                <Box mt={2}>
                  <Button
                    className="location-details__add-location-btn"
                    disabled={isAddDisabled}
                    onClick={handleAddLocation}
                  >
                    Add Location
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
        <Typography className="location-details__added-location-title">
          Added Locations
        </Typography>

        <Typography className="location-details__added-location-description">
          All your added service locations will be listed here.
        </Typography>

        {/* Added Locations Grid */}
        {addedLocations.length === 0 ? (
          <Typography className="location-details__empty-state">
            No Location Added
          </Typography>
        ) : (
          <Grid
            container
            spacing={2}
            className="location-details__locations-grid"
          >
            {addedLocations.map((location: any) => (
              <Grid item xs={12} sm={6} md={4} key={location.id}>
                <LocationCard
                  zipcode={location.zipcode}
                  city={location.city}
                  state={location.state}
                  onRemove={() => handleRemoveLocation(location.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Service Media */}
      <Box className="location-details__section location-details__section--media">
        <Typography className="location-details__title">Preneed Planner Media</Typography>

        {uploadedImages.length === 0 && (
          <Box
            className="location-details__upload-service-image"
            onClick={() => document.getElementById("upload-input")?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <FileUploadOutlined className="location-details__upload-icon" />
            <Typography className="location-details__upload-text-title">
              Upload Planner Images
            </Typography>
            <Typography className="location-details__upload-text-subtitle">
              Drag and drop or click to browse
            </Typography>
            <input
              type="file"
              multiple
              accept="image/*"
              id="upload-input"
              style={{ display: "none" }}
              onChange={(e) => handleImageUpload(e.target.files)}
            />
          </Box>
        )}

        {/* Upload & Grid */}
        <Box
          className={`location-details__upload-wrapper ${uploadedImages.length > 0 ? "has-images" : ""
            }`}
        >
          {uploadedImages.length > 0 && (
            <Box className="location-details__uploaded-images">
              <Typography className="location-details__uploaded-title">
                Uploaded Images
              </Typography>
              <Grid container spacing={2}>
                {uploadedImages.length > 0 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      className={`location-details__upload-service-image location-details__upload-service-image--compact ${isDragActive ? "drag-active" : ""
                        }`}
                      sx={{
                        maxHeight: "5rem",
                        margin: "0 !important",
                      }}
                      onClick={() =>
                        document.getElementById("upload-input")?.click()
                      }
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <FileUploadOutlined className="location-details__upload-icon" />
                      <Typography className="location-details__upload-text-title">
                        Upload Planner Images
                      </Typography>
                      <Typography className="location-details__upload-text-subtitle">
                        Drag and drop or click to browse
                      </Typography>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        id="upload-input"
                        style={{ display: "none" }}
                        onChange={(e) => handleImageUpload(e.target.files)}
                      />
                    </Box>
                  </Grid>
                )}
                {uploadedImages.map((img, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Box className="location-details__image-box">
                      <img
                        src={img.preview}
                        alt={img.file.name}
                        className="location-details__uploaded-img"
                      />
                      <IconButton
                        className="location-details__remove-img"
                        onClick={() => handleDeleteClick(idx, true)}
                        size="small"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                      <Typography className="location-details__img-name">
                        {img.file.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
        {(fileTypeError || fileSizeError.length > 0 || imageCountError) && (
          <Box className="location-details__error-message" sx={{ flexDirection: 'row !important', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {fileTypeError && <Typography>{fileTypeError}</Typography>}
              {fileSizeError.length > 0 &&
                fileSizeError.map((name, idx) => (
                  <Typography key={idx}>{name} exceeds 5MB</Typography>
                ))}
              {imageCountError && <Typography>{imageCountError}</Typography>}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setFileTypeError(null);
                setFileSizeError([]);
                setImageCountError(null);
              }}
              sx={{ color: '#b91c1c', padding: 0 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Upload Criteria */}
        <Box className="location-details__upload-criteria">
          <Box className="location-details__upload-header">
            <InfoOutlined className="location-details__info-icon" />
            <Typography className="location-details__upload-title">
              Image Upload Criteria
            </Typography>
          </Box>

          <ul className="location-details__upload-list">
            {UPLOAD_CRITERIA.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </Box>

        {/* Provider Gallery */}
        {gallery && gallery.length > 0 && (
          <Box className="location-details__gallery-section" mt={3}>
            <Typography className="location-details__title" mb={1}>
              Gallery
            </Typography>
            <Grid container spacing={2}>
              {gallery.map((img: any) => (
                <Grid item xs={6} sm={4} md={3} key={img.fileId}>
                  <Box className="location-details__image-box">
                    <img
                      src={img.url}
                      alt={img.fileName}
                      className="location-details__uploaded-img"
                    />
                    <IconButton
                      className="location-details__remove-img"
                      onClick={() =>
                        handleDeleteClick(-1, false, img.fileId)
                      }
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    <Typography className="location-details__img-name">
                      {img.fileName}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      <InfoPopup
        open={infoPopup.open}
        title={infoPopup.title}
        note={infoPopup.note}
        onClose={() => setInfoPopup((prev) => ({ ...prev, open: false }))}
      />

      <ActionConfirmationPopup
        open={deleteConfirm.open}
        title={deleteConfirm.type === "image" ? "Delete Image" : "Delete Location"}
        note={
          deleteConfirm.type === "image"
            ? "Are you sure you want to delete this image? This action cannot be undone."
            : "Are you sure you want to delete this location? This action cannot be undone."
        }
        onClose={() =>
          setDeleteConfirm({
            open: false,
            id: null,
            isLocal: false,
            index: -1,
            type: "",
          })
        }
        onConfirm={handleConfirmDelete}
        confirmButtonText="Delete"
        variant="danger"
        type="delete"
      />
    </Box>
  );
};

export default LocationDetails;
